import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, CalendarClock, CalendarX, Info, Loader2, Rocket, Trash2, Undo2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { getCampaign, updateCampaign, deleteCampaign, getLineAliases, unscheduleCampaign } from '../services/api';
import { useCampaignsConfig } from '../hooks/useCampaignsConfig';
import { useChannel } from '../context/ChannelContext';
import { CampaignStatusBadge } from '../components/campaigns/CampaignStatusBadge';
import { scheduleText } from '../components/campaigns/scheduleText';
import MemberList from '../components/campaigns/MemberList';
import ProblemsPanel from '../components/campaigns/ProblemsPanel';
import TabMap from '../components/campaigns/TabMap';
import LaunchDialog from '../components/campaigns/LaunchDialog';
import ScheduleDialog from '../components/campaigns/ScheduleDialog';
import RollbackDialog, { rollbackRemaining } from '../components/campaigns/RollbackDialog';
import RunHistory from '../components/campaigns/RunHistory';
import { waitForRun, ATTENTION_MSG } from '../components/campaigns/runPolling';
import { AddMenusDialog, ReplaceLiveDialog } from '../components/campaigns/AddMenusDialog';
import { useWorkspace } from '../context/WorkspaceContext';
import { useAuth } from '../context/AuthContext';

const LAUNCHABLE = ['draft', 'scheduled', 'failed', 'rolled_back', 'missed', 'live', 'superseded'];
const SCHEDULABLE = ['draft', 'scheduled', 'failed', 'rolled_back', 'missed', 'superseded'];
const DELETABLE = ['draft', 'failed', 'rolled_back', 'missed', 'cancelled', 'superseded'];

export const CampaignDetail = () => {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const config = useCampaignsConfig();
  const { selectedChannelId, selectChannel } = useChannel();
  const { isWorkspaceAdmin } = useWorkspace();
  const { isSuperAdmin } = useAuth();
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState('');
  const [aliases, setAliases] = useState([]);
  const [aliasStatus, setAliasStatus] = useState('loading');
  const nameFocused = useRef(false);
  const [addOpen, setAddOpen] = useState(false);
  const [replaceOpen, setReplaceOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const alive = useRef(true);
  const [launchOpen, setLaunchOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [rollbackOpen, setRollbackOpen] = useState(false);
  const [tracking, setTracking] = useState(null); // 'launch' | 'rollback' while we poll a run we started
  const [runsKey, setRunsKey] = useState(0);
  const [unscheduling, setUnscheduling] = useState(false);

  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);

  // Task 9 hooks into this: dialogs call reload() after changing the campaign.
  const reload = useCallback(async ({ silent = false } = {}) => {
    try {
      const c = await getCampaign(campaignId);
      if (!alive.current) return null;
      setCampaign(c);
      if (!nameFocused.current) setName(c.name); // don't clobber what the user is typing
      return c;
    } catch (error) {
      if (alive.current && !silent) toast.error(error.response?.data?.detail || 'Failed to load campaign');
      return null;
    } finally {
      if (alive.current) setLoading(false);
    }
  }, [campaignId]);

  useEffect(() => { setLoading(true); setCampaign(null); reload(); }, [reload]);

  // Opened by link or bookmark: the sidebar follows the campaign's channel, so edits never look
  // like they belong to another channel.
  const campaignChannelId = campaign?.channelId;
  useEffect(() => {
    if (campaignChannelId && campaignChannelId !== selectedChannelId) selectChannel(campaignChannelId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignChannelId]);

  // Follow a launch/rollback run we just started, toast the outcome, then refresh the page data.
  const trackRun = useCallback(async (runId, kind) => {
    const isRollback = kind === 'rollback';
    setTracking(kind);
    const tid = toast.loading(isRollback ? 'Rolling back…' : 'Launching…');
    try {
      const { run, timedOut, cancelled } = await waitForRun(runId, { isCancelled: () => !alive.current });
      if (cancelled || !alive.current) return;
      if (timedOut) {
        toast.info('Still finishing — this page will update');
      } else if (isRollback) {
        if (run.result === 'pass') toast.success('Rolled back');
        else toast.error("Rollback didn't finish — press Roll back again");
      } else if (run.result === 'pass') toast.success('Campaign is live');
      else if (run.result === 'rolled_back') toast.error('Launch failed — LINE was put back as it was');
      else toast.error('Launch failed');
      if (!timedOut && run.needsAttention) toast.error(ATTENTION_MSG, { duration: Infinity, closeButton: true });
    } finally {
      toast.dismiss(tid);
      if (alive.current) { setTracking(null); setRunsKey((k) => k + 1); }
    }
    await reload({ silent: true });
  }, [reload]);

  const unschedule = async () => {
    setUnscheduling(true);
    try {
      await unscheduleCampaign(campaignId);
      toast.success('Schedule removed');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unschedule');
    } finally {
      if (alive.current) setUnscheduling(false);
      await reload({ silent: true });
    }
  };

  const status = campaign?.status;
  const running = status === 'running';
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(() => reload({ silent: true }), 3000);
    return () => clearInterval(t);
  }, [running, reload]);

  const channelId = campaign?.channelId;
  const aliasKey = (campaign?.members || []).map((m) => `${m.id}:${m.richMenuAliasId || ''}`).join(',');
  useEffect(() => {
    if (!channelId) return undefined;
    let cancelled = false;
    setAliasStatus('loading');
    getLineAliases(channelId)
      .then((d) => { if (!cancelled) { setAliases(d.aliases || []); setAliasStatus('ok'); } })
      .catch(() => { if (!cancelled) { setAliases([]); setAliasStatus('error'); } });
    return () => { cancelled = true; };
  }, [channelId, aliasKey]);

  const save = async (body, errorMsg = 'Failed to save') => {
    setBusy(true);
    try {
      await updateCampaign(campaignId, body);
      await reload();
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || errorMsg);
      await reload();
      return false;
    } finally {
      if (alive.current) setBusy(false);
    }
  };

  if (!config.loading && !config.visible) return <Navigate to="/menus" replace />;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="campaign-detail">
        <div className="w-8 h-8 border-2 border-[#00E05E] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!campaign) {
    return (
      <div className="p-4 sm:p-6" data-testid="campaign-detail">
        <Link to="/campaigns" className="text-sm text-[#A1A1AA] hover:text-white inline-flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" /> Campaigns
        </Link>
        <p className="mt-6 text-[#A1A1AA]">Campaign not found.</p>
      </div>
    );
  }

  const locked = running;
  const launching = running || !!tracking;
  // Switched off for this workspace: nothing new can launch, but a live campaign can still be rolled back.
  const featureOff = !config.loading && !config.allowed;
  const canLaunch = LAUNCHABLE.includes(status) && !featureOff;
  const canSchedule = SCHEDULABLE.includes(status) && !featureOff;
  const rollbackLeft = status === 'live' ? rollbackRemaining(campaign.rollbackUntil) : null;

  const menuIds = campaign.menuIds || [];

  const saveName = () => {
    const trimmed = name.trim();
    if (!trimmed) { setName(campaign.name); return; }
    if (trimmed !== campaign.name) save({ name: trimmed }, 'Failed to rename');
  };
  const setDefault = (id) => save(id ? { defaultMenuId: id } : { clearDefault: true });
  const move = (i, dir) => {
    const next = [...menuIds];
    [next[i], next[i + dir]] = [next[i + dir], next[i]];
    save({ menuIds: next });
  };
  const remove = (id) => save({ menuIds: menuIds.filter((m) => m !== id) });

  const confirmDelete = async (e) => {
    if (e?.preventDefault) e.preventDefault(); // keep the dialog open until the request settles
    if (deleting) return;
    setDeleting(true);
    try {
      await deleteCampaign(campaignId);
      navigate('/campaigns');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete campaign');
      if (alive.current) { setDeleting(false); setDeleteOpen(false); }
    }
  };

  return (
    <div className="p-4 sm:p-6 max-w-5xl" data-testid="campaign-detail">
      <Link to="/campaigns" className="text-sm text-[#A1A1AA] hover:text-white inline-flex items-center gap-1 mb-4">
        <ArrowLeft className="w-4 h-4" /> Campaigns
      </Link>

      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-6">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <Input
              value={name}
              maxLength={100}
              disabled={locked}
              onChange={(e) => setName(e.target.value)}
              onFocus={() => { nameFocused.current = true; }}
              onBlur={() => { nameFocused.current = false; saveName(); }}
              onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
              className="bg-transparent border-transparent hover:border-[#27272A] focus:border-[#27272A] text-xl font-bold h-auto py-1 px-2 -ml-2 w-auto min-w-0 max-w-full sm:w-96"
              aria-label="Campaign name"
              data-testid="campaign-name-input"
            />
            <CampaignStatusBadge status={status} />
          </div>
          <p className="text-sm text-[#A1A1AA] mt-1" data-testid="campaign-channel">
            Campaign in <span className="text-[#06C755]">{campaign.channelName}</span>
            {status === 'scheduled' && campaign.schedule && (
              <span className="text-[#6366F1]"> · {scheduleText(campaign.schedule)}</span>
            )}
          </p>
        </div>
        <div className="flex flex-wrap gap-2" data-testid="campaign-actions">
          {rollbackLeft && (
            <Button variant="outline" className="border-[#27272A]" disabled={launching} onClick={() => setRollbackOpen(true)} data-testid="rollback-button">
              <Undo2 className="w-4 h-4 mr-2" /> Roll back
              <span className="ml-2 text-xs text-[#A1A1AA]">available for {rollbackLeft}</span>
            </Button>
          )}
          {status === 'scheduled' && (
            <Button variant="outline" className="border-[#27272A]" disabled={unscheduling} onClick={unschedule} data-testid="unschedule-button">
              {unscheduling ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CalendarX className="w-4 h-4 mr-2" />} Unschedule
            </Button>
          )}
          {canSchedule && (
            <Button variant="outline" className="border-[#27272A]" disabled={launching} onClick={() => setScheduleOpen(true)} data-testid="schedule-button">
              <CalendarClock className="w-4 h-4 mr-2" /> {status === 'scheduled' ? 'Reschedule' : 'Schedule'}
            </Button>
          )}
          {(canLaunch || running) && (
            <Button className="bg-[#06C755] hover:bg-[#05b34c] text-black" disabled={launching} onClick={() => setLaunchOpen(true)} data-testid="launch-button">
              <Rocket className="w-4 h-4 mr-2" /> {status === 'live' ? 'Launch again' : 'Launch now'}
            </Button>
          )}
        </div>
      </div>

      {featureOff && (
        <div className="text-sm text-[#A1A1AA] bg-[#18181B] border border-[#27272A] rounded-lg px-3 py-2 mb-4" data-testid="campaigns-off-info">
          {config.reason || 'Campaigns are not enabled for this workspace'}. Launching and scheduling are unavailable{status === 'live' ? '; you can still roll back' : ''}.
        </div>
      )}

      {launching && (
        <div className="flex items-center gap-2 text-sm text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2 mb-4" data-testid="campaign-launching-banner">
          <Loader2 className="w-4 h-4 animate-spin flex-shrink-0" /> Launching… LINE is being updated. This can take a minute.
        </div>
      )}

      {locked && (
        <div className="flex items-center gap-2 text-sm text-[#A1A1AA] bg-[#18181B] border border-[#27272A] rounded-lg px-3 py-2 mb-4" data-testid="campaign-locked-info">
          <Info className="w-4 h-4 flex-shrink-0" /> Editing is locked while the campaign is launching.
        </div>
      )}

      <div className="space-y-4">
        <MemberList
          campaign={campaign}
          locked={locked}
          busy={busy}
          onSetDefault={setDefault}
          onMove={move}
          onRemove={remove}
          onAdd={() => setAddOpen(true)}
          onReplace={() => setReplaceOpen(true)}
        />
        <ProblemsPanel campaignId={campaignId} lastProblems={campaign.lastProblems} resetKey={`${menuIds.join(',')}|${campaign.defaultMenuId || ''}`} />
        <TabMap members={campaign.members || []} aliases={aliases} aliasStatus={aliasStatus} />

        <div data-testid="campaign-runs-section">
          <RunHistory campaignId={campaignId} members={campaign.members} refreshKey={`${runsKey}|${campaign.lastRunId || ''}|${status}`} />
        </div>

        {/* Deleting is admin-only on the server */}
        {(isWorkspaceAdmin || isSuperAdmin) && DELETABLE.includes(status) && (
          <div className="pt-2">
            <Button variant="outline" className="border-red-500/40 text-red-400 hover:bg-red-500/10" onClick={() => setDeleteOpen(true)} data-testid="delete-campaign-button">
              <Trash2 className="w-4 h-4 mr-2" /> Delete campaign
            </Button>
          </div>
        )}
      </div>

      <AddMenusDialog open={addOpen} onOpenChange={setAddOpen} campaign={campaign} onSaved={reload} />
      <ReplaceLiveDialog open={replaceOpen} onOpenChange={setReplaceOpen} campaign={campaign} onSaved={reload} />

      <LaunchDialog open={launchOpen} onOpenChange={setLaunchOpen} campaign={campaign} onStarted={(runId) => { reload({ silent: true }); trackRun(runId, 'launch'); }} />
      <RollbackDialog open={rollbackOpen} onOpenChange={setRollbackOpen} campaign={campaign} onStarted={(runId) => { reload({ silent: true }); trackRun(runId, 'rollback'); }} />
      <ScheduleDialog open={scheduleOpen} onOpenChange={setScheduleOpen} campaign={campaign} maxLatenessMinutes={config.maxLatenessMinutes} onScheduled={() => reload({ silent: true })} />

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent className="bg-[#18181B] border-[#27272A]">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this campaign?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#A1A1AA]">
              "{campaign.name}" will be removed from your campaigns. The menus themselves are kept.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#27272A]">Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={confirmDelete} disabled={deleting} data-testid="delete-campaign-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CampaignDetail;
